import {IRequestAuth} from 'core/api'
import {
  clearState as clearStateAction,
} from 'core/store/enhancers/clearStateEnhance'
import {all, call, fork, put, select} from 'redux-saga/effects'
import {
  historyPushRequest,
  saga as historySaga,
} from './history'
import {
  ISagaDependencies as ILoginSagaDependencies,
  saga as loginSaga,
  tokenSet,
} from './login'
import {actions as mainActions, saga as mainSaga} from './main'
import {selectToken as selectTokenSelector} from './selectors'
import {
  Dependencies as StudyCoordinatorDependencies,
  saga as studyCoordinatorSaga,
} from './study-coordinator'
import {
  IMainSagaDependencies as ISubjectSagaDependencies,
  saga as subjectsSaga,
} from './subjects'

/**
 * Wraps select for selectToken to keep conventions for dependencies to be invoked via `yield call(dependency)`
 */
export function* selectToken() {
  return yield select(selectTokenSelector)
}

/**
 * Wraps dispatch of clearState to keep conventions for dependencies to be invoked via `yield call(dependency)`
 */
export function* clearState() {
  yield put(clearStateAction())
}

/**
 * Wraps an API request with authentication logic.
 * Calls the API request fn with the token from the redux store as the first argument.
 * Sets the new token if given in the response at `response.headers.Authorization` or
 * `response.headers.authorization`.
 *
 * ex: `yield call(withAuth, myApiCall, arg1ToMyApiCall)`
 *
 * @param fn some authenticated API request with token as first arg
 * @param args args to pass to authenticated API request
 */
export function* withAuth(fn: () => any, ...args: any[]) {
  const token = yield call(selectToken)
  const res = yield call(fn, token, ...args)
  const authHeader = res && res.headers && (res.headers.Authorization || res.headers.authorization)
  if (authHeader && typeof authHeader === 'string' && authHeader.startsWith('Bearer ')) {
    const newToken = authHeader.replace(/^Bearer /, '')
    yield put(tokenSet({ token: newToken }))
  }
  return res
}

/**
 * Wraps an API request with authentication logic.
 * Calls the API request fn with the token from the redux store in an IRequestAuth as the first argument.
 * Sets the new token if given in the response at `response.headers.Authorization` or
 * `response.headers.authorization`.
 *
 * ex: `yield call(withAuth, myApiCall, arg1ToMyApiCall)`
 *
 * @param fn some authenticated API request with an IRequestAuth as first arg
 * @param args args to pass to authenticated API request
 */
export function* withTokenAuth(fn: (auth: IRequestAuth, ...args: any[]) => any, ...args: any[]) {
  const token = yield call(selectToken)
  const res = yield call(fn, {token}, ...args)
  const authHeader = res && res.headers && (res.headers.Authorization || res.headers.authorization)
  if (authHeader && typeof authHeader === 'string' && authHeader.startsWith('Bearer ')) {
    const newToken = authHeader.replace(/^Bearer /, '')
    yield put(tokenSet({ token: newToken }))
  }
  return res
}

function* onTokenExpired() {
  yield call(clearState)
  // TODO I18N
  yield put(mainActions.snackbarSetRequest('Your session has expired, please log in and try again.'))
}

function* redirectTo(path: string) {
  yield put(historyPushRequest(path))
}

export default function* saga(): any {
  yield all([
    fork<ILoginSagaDependencies>(loginSaga, {selectToken, clearState, withAuth, onTokenExpired}),
    fork<ISubjectSagaDependencies>(subjectsSaga, {withAuth, withTokenAuth, onTokenExpired, redirectTo}),
    fork(mainSaga),
    fork(historySaga),
    fork<StudyCoordinatorDependencies>(studyCoordinatorSaga, {withAuth, onTokenExpired}),
  ])
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/store/saga.ts